import { DEMO_BRAND as C, useScrollReveal } from './_hooks';

/* ── Three concrete promises. Each gets its own mini visual.
   We avoid generic icon-stacks; each column has a custom motif. ── */

interface PromiseCardProps {
  index: number;
  num: string;
  title: string;
  body: string;
  accent: string;
  accentBg: string;
  visual: React.ReactNode;
}

function PromiseCard({ index, num, title, body, accent, accentBg, visual }: PromiseCardProps) {
  const [ref, visible] = useScrollReveal(0.2);

  return (
    <div
      ref={ref}
      className="group relative flex flex-col rounded-[28px] border border-[#0a1024]/[0.06] bg-white p-7 transition-all duration-500"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(28px)',
        transitionDelay: `${index * 110}ms`,
        boxShadow: visible
          ? '0 1px 2px rgba(13,27,62,0.04), 0 16px 40px -24px rgba(13,27,62,0.16)'
          : '0 0 0 rgba(0,0,0,0)',
      }}>
      {/* Numbered tab */}
      <div className="flex items-center gap-3">
        <span
          className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-semibold"
          style={{ background: accentBg, color: accent }}>
          {num}
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
          You will see
        </span>
      </div>

      {/* Visual canvas */}
      <div
        className="mt-6 flex h-[180px] items-center justify-center overflow-hidden rounded-2xl"
        style={{ background: accentBg + '4d' }}>
        {visual}
      </div>

      {/* Copy */}
      <h3
        className="mt-7 text-[26px] leading-[1.1] tracking-tight text-[#0a1024]"
        style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}>
        {title}
      </h3>
      <p className="mt-3 text-[15px] leading-relaxed text-slate-500">{body}</p>
    </div>
  );
}

/* ── Visual 1: Document → Citation chip ── */
function IngestVisual() {
  return (
    <svg viewBox="0 0 240 160" className="h-full w-full px-6">
      {/* Document */}
      <g style={{ animation: 'doc-slide 4s ease-in-out infinite' }}>
        <rect x="20" y="22" width="78" height="100" rx="6" fill="white" stroke={C.hairline} />
        <rect x="28" y="32" width="50" height="3" rx="1.5" fill={C.blue} opacity="0.3" />
        <rect x="28" y="42" width="62" height="2" rx="1" fill={C.muted} opacity="0.4" />
        <rect x="28" y="48" width="58" height="2" rx="1" fill={C.muted} opacity="0.4" />
        <rect x="28" y="54" width="48" height="2" rx="1" fill={C.muted} opacity="0.4" />
        <rect x="28" y="64" width="62" height="2" rx="1" fill={C.muted} opacity="0.4" />
        <rect x="28" y="70" width="40" height="2" rx="1" fill={C.muted} opacity="0.4" />
        {/* Highlighted clause */}
        <rect
          x="28"
          y="82"
          width="58"
          height="10"
          rx="2"
          fill={C.amberSoft}
          style={{ animation: 'highlight-pulse 3s ease-in-out infinite' }}
        />
        <rect x="28" y="84" width="50" height="2" rx="1" fill={C.amber} opacity="0.7" />
        <rect x="28" y="88" width="40" height="2" rx="1" fill={C.amber} opacity="0.5" />
      </g>

      {/* Arrow */}
      <g stroke={C.blue} strokeWidth="1.5" fill="none" strokeDasharray="3 3">
        <path d="M 106 72 Q 130 72 140 72">
          <animate
            attributeName="stroke-dashoffset"
            from="0"
            to="-12"
            dur="1.5s"
            repeatCount="indefinite"
          />
        </path>
        <path d="M 136 68 L 142 72 L 136 76" strokeDasharray="0" />
      </g>

      {/* Citation chip */}
      <g style={{ animation: 'chip-pop 4s ease-in-out infinite' }}>
        <rect x="150" y="56" width="78" height="32" rx="16" fill={C.blue} />
        <circle cx="166" cy="72" r="6" fill="white" />
        <text x="166" y="75" textAnchor="middle" fontSize="8" fontWeight="700" fill={C.blue}>
          ¶
        </text>
        <rect x="178" y="66" width="40" height="2.5" rx="1.25" fill="white" opacity="0.9" />
        <rect x="178" y="72" width="32" height="2.5" rx="1.25" fill="white" opacity="0.5" />
      </g>

      <style>{`
        @keyframes doc-slide {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(-3px); }
        }
        @keyframes highlight-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes chip-pop {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.04); }
        }
      `}</style>
    </svg>
  );
}

/* ── Visual 2: Multi-LLM swap with active rotation ── */
function ModelSwapVisual() {
  const models = [
    { name: 'Claude', color: '#c08838' },
    { name: 'GPT-4o', color: '#16a34a' },
    { name: 'Gemini', color: '#6366f1' },
    { name: 'DeepSeek', color: '#0ea5e9' },
  ];

  return (
    <div className="relative h-full w-full px-6">
      <div className="absolute inset-x-6 top-1/2 -translate-y-1/2">
        <div className="rounded-2xl border border-[#0a1024]/[0.06] bg-white p-3 shadow-sm">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              Model
            </span>
            <div className="flex gap-1">
              <span className="h-1 w-1 rounded-full bg-emerald-400" />
              <span className="text-[9px] font-semibold text-emerald-600">live</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {models.map((m, i) => (
              <div
                key={m.name}
                className="rounded-md px-2.5 py-1 text-[11px] font-semibold transition-all"
                style={{
                  background: '#f8fafc',
                  color: C.mid,
                  border: '1px solid #e2e8f0',
                  animation: `model-cycle 8s linear infinite`,
                  animationDelay: `${i * 2}s`,
                }}>
                {m.name}
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ background: C.blue, animation: 'pulse-soft 1.4s ease-in-out infinite' }}
            />
            <div className="h-1 flex-1 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full"
                style={{
                  background: `linear-gradient(90deg, ${C.blue}, ${C.indigo})`,
                  width: '38%',
                  animation: 'stream-progress 3s ease-in-out infinite',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes model-cycle {
          0%, 18% { background: #f8fafc; color: ${C.mid}; transform: scale(1); }
          22%, 40% { background: ${C.blue}; color: white; transform: scale(1.05); }
          44%, 100% { background: #f8fafc; color: ${C.mid}; transform: scale(1); }
        }
        @keyframes stream-progress {
          0%, 100% { width: 12%; }
          50% { width: 78%; }
        }
        @keyframes pulse-soft {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.4); opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}

/* ── Visual 3: Deployment surfaces (widget, API, portal) ── */
function DeployVisual() {
  return (
    <svg viewBox="0 0 240 160" className="h-full w-full px-4">
      {/* Hub */}
      <g>
        <circle
          cx="120"
          cy="80"
          r="22"
          fill={C.blue}
          style={{ filter: 'drop-shadow(0 4px 12px rgba(0,71,144,0.3))' }}
        />
        <text x="120" y="84" textAnchor="middle" fontSize="9" fontWeight="700" fill="white">
          agent
        </text>
      </g>

      {/* Lines */}
      <g stroke={C.blue} strokeWidth="1.2" fill="none" opacity="0.4" strokeDasharray="3 3">
        <path d="M 102 70 L 50 38" />
        <path d="M 138 70 L 190 38" />
        <path d="M 120 102 L 120 134" />
      </g>

      {/* Surfaces */}
      <g>
        {/* Widget */}
        <rect x="22" y="20" width="60" height="36" rx="6" fill="white" stroke={C.hairline} />
        <rect x="28" y="26" width="32" height="2" rx="1" fill={C.mid} opacity="0.4" />
        <rect x="28" y="32" width="24" height="2" rx="1" fill={C.mid} opacity="0.3" />
        <rect x="28" y="42" width="48" height="10" rx="5" fill={C.blueLight} />
        <text x="52" y="49" textAnchor="middle" fontSize="7" fontWeight="700" fill={C.blue}>
          widget
        </text>
      </g>

      <g>
        {/* API */}
        <rect x="160" y="20" width="58" height="36" rx="6" fill={C.dark} />
        <text x="167" y="32" fontSize="7" fontFamily="monospace" fill="#c084fc">
          POST
        </text>
        <text x="186" y="32" fontSize="7" fontFamily="monospace" fill="#93c5fd">
          /chat
        </text>
        <text x="167" y="44" fontSize="6" fontFamily="monospace" fill="#94a3b8">
          {'{ query: ... }'}
        </text>
        <rect x="190" y="48" width="22" height="3" rx="1.5" fill="#10b981" />
      </g>

      <g>
        {/* Portal */}
        <rect x="86" y="118" width="68" height="36" rx="6" fill="white" stroke={C.hairline} />
        <circle cx="100" cy="136" r="5" fill={C.amber} opacity="0.3" />
        <rect x="108" y="130" width="36" height="2.5" rx="1.25" fill={C.mid} opacity="0.6" />
        <rect x="108" y="136" width="28" height="2" rx="1" fill={C.mid} opacity="0.3" />
        <rect x="108" y="142" width="20" height="2" rx="1" fill={C.mid} opacity="0.3" />
      </g>

      <style>{`
        @keyframes orbit-ping {
          0% { r: 22; opacity: 0.6; }
          100% { r: 36; opacity: 0; }
        }
      `}</style>
      <circle cx="120" cy="80" r="22" fill="none" stroke={C.blue} strokeWidth="1">
        <animate attributeName="r" from="22" to="40" dur="2.4s" repeatCount="indefinite" />
        <animate attributeName="opacity" from="0.4" to="0" dur="2.4s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

const promises = [
  {
    num: '01',
    title: 'Your real documents, ingested live.',
    body: 'Drop three files into the builder. Watch chunking, embeddings, and citation graphs assemble in seconds — yes, in the call.',
    accent: C.blue,
    accentBg: C.blueLight,
    visual: <IngestVisual />,
  },
  {
    num: '02',
    title: 'Swap the model mid-conversation.',
    body: 'Same question, Claude → GPT-4o → Gemini, side by side. You decide which one belongs to your team — not a vendor.',
    accent: C.indigo,
    accentBg: '#eef0ff',
    visual: <ModelSwapVisual />,
  },
  {
    num: '03',
    title: 'Deploy before the call ends.',
    body: 'Widget snippet, API call, internal portal — all wired in one click. We hand you the keys, not a roadmap.',
    accent: C.amber,
    accentBg: C.amberSoft,
    visual: <DeployVisual />,
  },
];

const DemoWhatYouSee: React.FC = () => {
  const [headerRef, headerVisible] = useScrollReveal(0.4);

  return (
    <section className="relative bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header
          ref={headerRef}
          className="mx-auto max-w-3xl text-center transition-all duration-700"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(16px)',
          }}>
          <div className="inline-flex items-center gap-3">
            <span className="inline-block h-px w-8 bg-[#0a1024]/40" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#0a1024]/60">
              The agenda
            </span>
            <span className="inline-block h-px w-8 bg-[#0a1024]/40" />
          </div>
          <h2
            className="mt-6 text-[36px] leading-[1.05] tracking-tight text-[#0a1024] sm:text-[48px] lg:text-[56px]"
            style={{ fontFamily: 'Fraunces, serif', fontWeight: 400 }}>
            Three things you&apos;ll watch happen.
            <br />
            <span
              style={{
                fontFamily: 'Fraunces, serif',
                fontStyle: 'italic',
                fontWeight: 500,
                fontVariationSettings: '"SOFT" 100, "opsz" 144',
                color: '#004790',
              }}>
              Not three things we&apos;ll claim.
            </span>
          </h2>
        </header>

        <div className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {promises.map((p, i) => (
            <PromiseCard
              key={p.num}
              index={i}
              num={p.num}
              title={p.title}
              body={p.body}
              accent={p.accent}
              accentBg={p.accentBg}
              visual={p.visual}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default DemoWhatYouSee;
